import { useMemo, useState } from 'react';
import DataTable, { Column } from '@/components/common/DataTable';
import type { LedgerEntry } from '@/types/member';

const typeStyles: Record<string, { label: string; icon: string; classes: string }> = {
  earn: { label: 'Earn', icon: 'ri-add-line', classes: 'bg-accent-100 text-accent-800' },
  redeem: { label: 'Redeem', icon: 'ri-gift-2-line', classes: 'bg-secondary-100 text-secondary-800' },
  adjust: { label: 'Adjust', icon: 'ri-equalizer-line', classes: 'bg-background-200 text-foreground-700' },
};

interface LedgerTableProps {
  data: LedgerEntry[];
}

export default function LedgerTable({ data }: LedgerTableProps) {
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);

  const columns: Column<LedgerEntry>[] = useMemo(
    () => [
      {
        key: 'id',
        header: 'Txn ID',
        title: 'Transaction ID',
        sortable: true,
        render: (entry) => (
          <span className="font-mono text-xs text-foreground-700 bg-background-100/90 px-2 py-0.5 rounded-md border border-background-200/70 whitespace-nowrap">
            {entry.id}
          </span>
        ),
      },
      {
        key: 'member',
        header: 'Member',
        title: 'Member',
        sortable: true,
        render: (entry) => (
          <span className="font-semibold text-foreground-950 whitespace-nowrap">
            {entry.member}
          </span>
        ),
      },
      {
        key: 'type',
        header: 'Type',
        title: 'Event Type',
        sortable: true,
        render: (entry) => {
          const style = typeStyles[entry.type] ?? typeStyles.adjust;
          return (
            <span
              className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-0.5 rounded-full whitespace-nowrap shadow-2xs ${style.classes}`}
            >
              <i className={style.icon}></i>
              {style.label}
            </span>
          );
        },
      },
      {
        key: 'points',
        header: 'Points',
        title: 'Points Amount',
        sortable: true,
        align: 'right',
        sortValue: (e) => e.points,
        render: (entry) => {
          const positive = entry.points > 0;
          return (
            <span
              className={`font-bold tabular-nums whitespace-nowrap ${
                positive ? 'text-accent-700' : 'text-secondary-700'
              }`}
            >
              {positive ? '+' : ''}
              {entry.points.toLocaleString()}
            </span>
          );
        },
      },
      {
        key: 'balanceAfter',
        header: 'Balance After',
        title: 'Balance After Event',
        sortable: true,
        align: 'right',
        sortValue: (e) => e.balanceAfter,
        render: (entry) => (
          <span className="text-foreground-800 font-medium tabular-nums whitespace-nowrap">
            {entry.balanceAfter.toLocaleString()}
          </span>
        ),
      },
      {
        key: 'source',
        header: 'Source',
        title: 'Integration Source',
        sortable: true,
        render: (entry) => (
          <span className="text-foreground-700 text-xs font-medium whitespace-nowrap bg-background-100/70 px-2 py-0.5 rounded border border-background-200/60">
            {entry.source}
          </span>
        ),
      },
      {
        key: 'description',
        header: 'Description',
        title: 'Description',
        render: (entry) => (
          <span className="text-foreground-600 line-clamp-1 max-w-xs">{entry.description}</span>
        ),
      },
      {
        key: 'time',
        header: 'When',
        title: 'Timestamp',
        align: 'right',
        render: (entry) => (
          <span className="text-foreground-400 text-xs whitespace-nowrap">{entry.time}</span>
        ),
      },
    ],
    []
  );

  return (
    <DataTable
      data={data}
      columns={columns}
      getRowId={(e) => e.id}
      selectable={true}
      selectedIds={selectedIds}
      onSelectionChange={(ids) => setSelectedIds(ids)}
      columnVisibility={true}
      exportable={true}
      exportFilename="points-ledger.csv"
      pageSize={10}
      pageSizeOptions={[10, 20, 50]}
      minWidth="920px"
      emptyIcon="ri-history-line"
      emptyTitle="No ledger events found"
      emptyDescription="Try adjusting your search term or clearing your filters."
    />
  );
}
